import React, { useEffect, useMemo, useState } from "react";
import FormDialog from "../../../../components/common/FormDialog";
import FormField from "../../../../components/common/FormField";
import ChipAutocomplete from "../../../../components/common/ChipAutocomplete";
import useFormValidation from "../../../../hooks/useFormValidation";
import { required } from "../../../../utils/formValidation";

const schema = {
  name: [required("Permission name is required.")],
};

const PermissionFormDialog = ({
  open,
  mode = "create",
  permission = null,
  suggestions = [],
  loading = false,
  onClose,
  onSubmit,
}) => {
  const isCreate = mode === "create";
  const [formError, setFormError] = useState("");
  const [allow, setAllow] = useState([]);
  const [deny, setDeny] = useState([]);

  const { values, getFieldProps, validateAll, reset } = useFormValidation(
    { name: "" },
    schema
  );

  useEffect(() => {
    if (!open) return;
    setFormError("");
    reset({ name: permission?.name || "" });
    setAllow(Array.isArray(permission?.allow) ? [...permission.allow] : []);
    setDeny(Array.isArray(permission?.deny) ? [...permission.deny] : []);
  }, [open, permission, reset]);

  const options = useMemo(
    () =>
      Array.from(
        new Set([...(suggestions || []), ...allow, ...deny].filter(Boolean))
      ),
    [allow, deny, suggestions]
  );

  const handleSubmit = async () => {
    const { isValid, errors } = validateAll();
    if (!isValid) {
      setFormError(errors.name || "Please correct the highlighted fields.");
      return;
    }

    setFormError("");
    try {
      if (isCreate) {
        await onSubmit({ name: values.name.trim() });
      } else {
        await onSubmit({
          name: values.name.trim(),
          allow,
          deny,
        });
      }
    } catch (err) {
      setFormError(err?.message || "Unable to save permission.");
    }
  };

  return (
    <FormDialog
      open={open}
      title={isCreate ? "Add Permission" : "Edit Permission"}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      error={formError}
      onClearError={() => setFormError("")}
      submitLabel={isCreate ? "Create Permission" : "Save Changes"}
      loadingLabel="Saving..."
    >
      <FormField
        label="Permission Name"
        required
        disabled={loading}
        {...getFieldProps("name")}
      />
      {!isCreate ? (
        <>
          <ChipAutocomplete
            label="Allow"
            freeSolo
            options={options}
            value={allow}
            onChange={setAllow}
            disabled={loading}
            helperText="Example: space.s1"
          />
          <ChipAutocomplete
            label="Deny (optional)"
            freeSolo
            options={options}
            value={deny}
            onChange={setDeny}
            disabled={loading}
            helperText="Optional. Leave empty if nothing should be denied."
          />
        </>
      ) : null}
    </FormDialog>
  );
};

export default PermissionFormDialog;
